import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Edit, X, Save, Printer } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useReferral,
  useAcceptReferral,
  useUpdateReferral,
  useAssignReferral,
  useReceivedReferralAttachments,
  useReferralLetterReceiver,
  useRevealReferralCpr,
  type Referral,
  type ReferralAnswerCcReceiver,
  type ReferralAttachment,
  type ReferralPatientAddress,
  type ReferralPatientPhone,
  fetchReceivedReferralAttachments,
  fetchReferralLetterReceiver,
  type ReceivedReferralAttachment,
  type ReferralLetterReceiver,
} from '@/hooks/useReferrals';
import { isAcceptableCpr } from '@/lib/cpr';
import { ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/ui/PageLayout';
import { EntityTasksPanel } from '@/components/tasks/EntityTasksPanel';
import { PAGE_TITLE } from '@/components/ui/pageChrome';
import { ReferralAssignmentPanel } from '@/components/referrals/ReferralAssignmentPanel';
import { ReferralCprBadge } from '@/components/referrals/ReferralCprBadge';
import { ReceivedAttachmentFile } from '@/components/referrals/ReceivedAttachmentFile';
import { MedcomXref01Details } from '@/components/referrals/MedcomXref01Details';
import { ReferralRevisitPanel } from '@/components/referrals/ReferralRevisitPanel';
import { ReferralReplyPanel } from '@/components/referrals/ReferralReplyPanel';
import { EpikrisePanel } from '@/components/referrals/EpikrisePanel';
import { buildReferralPrintHtml, printHtml } from '@/lib/referral/referralPrint';
import { canRevealReferralCpr } from '@/lib/referral/referralCprReveal';
import { normalizeObjectIdentifier } from '@/lib/referral/attachmentViewer';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { DanishDateTimeField } from '@/components/ui/DanishDateTimeField';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { fromDatetimeLocal, toDatetimeLocal } from '@/lib/datetimeLocal';
import { formatReferralDate } from '@/lib/referral/referralDateTime';
import { isMedcomReceivedReferral } from '@/lib/referral/medcomReceivedFields';
import {
  formatMedcomIdentifier,
  referralSenderIdentity,
  senderIdentifierHeadingKey,
} from '@/lib/referral/senderIdentifier';
import { ReferralStatusBadge } from './Referrals';
import { ReferralFilesTab } from '@/components/referrals/ReferralFilesTab';
import { ReferralSagsakterTab } from '@/components/referrals/ReferralSagsakterTab';
import { LetterDetailBlock, ReferralLetterDetails } from '@/components/referrals/ReferralLetterDetails';
import { ReferralOverviewCard } from '@/components/referrals/ReferralOverviewCard';
import { RelationsTab } from '@/components/patient/RelationsTab';
import { ReferralLprTab } from '@/components/lpr/ReferralLprTab';

export function ReferralDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: referral, isLoading, error } = useReferral(id);
  const { hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const canRevealCpr = canRevealReferralCpr(
    (clinicId) => hasPermission(clinicId, 'patients_cpr', 'view'),
    activeClinicId,
    referral?.clinic_id,
  );
  const revealCpr = useRevealReferralCpr();
  const [printing, setPrinting] = useState(false);
  const acceptReferral = useAcceptReferral();
  const updateReferral = useUpdateReferral();
  const assignReferral = useAssignReferral();

  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [referrerName, setReferrerName] = useState('');
  const [referrerType, setReferrerType] = useState('');
  const [referrerReference, setReferrerReference] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cpr, setCpr] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [secretaryName, setSecretaryName] = useState('');
  const [psychologistName, setPsychologistName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [senderHospital, setSenderHospital] = useState('');
  const [senderDepartment, setSenderDepartment] = useState('');
  const [sentAt, setSentAt] = useState('');
  const [referringDoctorName, setReferringDoctorName] = useState('');
  const [referringDoctorSor, setReferringDoctorSor] = useState('');
  const [parent1Name, setParent1Name] = useState('');
  const [parent1Cpr, setParent1Cpr] = useState('');
  const [parent2Name, setParent2Name] = useState('');
  const [parent2Cpr, setParent2Cpr] = useState('');
  const [referralDiagnosis, setReferralDiagnosis] = useState('');
  const [ambulant, setAmbulant] = useState<'' | 'true' | 'false'>('');
  const [requestedExamination, setRequestedExamination] = useState('');
  const [anamnese, setAnamnese] = useState('');

  // Active tab
  type TabType = 'details' | 'sagsakter' | 'files' | 'relations' | 'lpr' | 'assignment' | 'medcom';
  const [activeTab, setActiveTab] = useState<TabType>('details');

  useEffect(() => {
    if (!referral) return;
    setReferrerName(referral.referrer_name ?? '');
    setReferrerType(referral.referrer_type ?? '');
    setReferrerReference(referral.referrer_reference ?? '');
    setFirstName(referral.first_name);
    setLastName(referral.last_name);
    setCpr('');
    setPhone(referral.phone ?? '');
    setEmail(referral.email ?? '');
    setReason(referral.reason ?? '');
    setNotes(referral.notes ?? '');
    setSecretaryName(referral.secretary_name ?? '');
    setPsychologistName(referral.psychologist_name ?? '');
    setIdNumber(referral.id_number ?? '');
    setCaseNumber(referral.case_number ?? '');
    setSenderHospital(referral.sender_hospital ?? '');
    setSenderDepartment(referral.sender_department ?? '');
    setSentAt(toDatetimeLocal(referral.sent_at));
    setReferringDoctorName(referral.referring_doctor_name ?? '');
    setReferringDoctorSor(referral.referring_doctor_sor ?? '');
    setParent1Name(referral.parent_1_name ?? '');
    setParent1Cpr('');
    setParent2Name(referral.parent_2_name ?? '');
    setParent2Cpr('');
    setReferralDiagnosis(referral.referral_diagnosis ?? '');
    setAmbulant(
      referral.ambulant === true ? 'true' : referral.ambulant === false ? 'false' : '',
    );
    setRequestedExamination(referral.requested_examination ?? '');
    setAnamnese(referral.anamnese ?? '');
  }, [referral]);

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      </PageLayout>
    );
  }

  if (error || !referral) {
    return (
      <PageLayout>
        <ErrorAlert message={t('referrals.error_not_found')} />
      </PageLayout>
    );
  }

  const medcomReceived = isMedcomReceivedReferral(referral);
  const medcomLockHint = t('referrals.medcom_received_field_locked');

  async function handleAccept() {
    if (!referral) return;
    if (!confirm(t('referrals.confirm_accept'))) return;
    try {
      const result = await acceptReferral.mutateAsync({ referral_id: referral.id });
      toast.success(t('referrals.toast_accepted'));
      navigate(`/patients/${result.patient_id}`);
    } catch (err) {
      console.error('accept referral failed', err);
      toast.error(t('referrals.accept_error'));
    }
  }

  async function handleDecline() {
    if (!referral) return;
    if (!confirm(t('referrals.confirm_decline'))) return;
    try {
      await updateReferral.mutateAsync({ referral_id: referral.id, status: 'declined' });
      toast.success(t('referrals.toast_declined'));
    } catch (err) {
      console.error('decline referral failed', err);
      toast.error(t('referrals.decline_error'));
    }
  }

  // C7: print with patient name + CPR on every page. The CPR is fetched through
  // decrypt-referral-cpr (audited, purpose referral_print) only when printing,
  // and only for users who may see it; it is never stored or logged here.
  async function handlePrint() {
    if (!referral) return;
    setPrinting(true);
    try {
      let cpr: string | null = null;
      if (referral.has_cpr && canRevealCpr) {
        const revealed = await revealCpr.mutateAsync({
          referral_id: referral.id,
          subject: 'patient',
          source: 'referral-print',
          purpose: 'referral_print',
        });
        cpr = revealed.cpr;
      }
      // C7: Modtager (from the stored letter) and the Bilag status for the print.
      // Neither carries the CPR. A failed read still prints, marked as such.
      let receiver: ReferralLetterReceiver | null = null;
      let receiverFailed = false;
      let receivedAttachments: ReceivedReferralAttachment[] | undefined;
      if (referral.source === 'medcom') {
        const xmlReceiver = referral.medcom_details.receiver;
        const hasXmlReceiver =
          typeof xmlReceiver === 'object' && xmlReceiver !== null && Object.keys(xmlReceiver).length > 0;
        const [receiverResult, attachmentsResult] = await Promise.allSettled([
          hasXmlReceiver ? Promise.resolve(null) : fetchReferralLetterReceiver(referral.id),
          referral.attachments.some((a) => Boolean(a.objectIdentifier))
            ? fetchReceivedReferralAttachments(referral.id)
            : Promise.resolve([] as ReceivedReferralAttachment[]),
        ]);
        if (receiverResult.status === 'fulfilled') receiver = receiverResult.value;
        else receiverFailed = true;
        if (attachmentsResult.status === 'fulfilled') receivedAttachments = attachmentsResult.value;
      }
      const html = buildReferralPrintHtml(referral, (key, options) => String(t(key, options)), {
        receiver,
        receiverFailed,
        receivedAttachments,
        cpr,
        cprPlaceholder: referral.has_cpr
          ? t('referrals.print_cpr_hidden')
          : t('referrals.print_cpr_none'),
        printedAt: new Date(),
      });
      printHtml(html);
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('referrals.print_failed'));
    } finally {
      setPrinting(false);
    }
  }

  async function handleAssign(clinicianId: string | null) {
    if (!referral) return;
    try {
      await assignReferral.mutateAsync({
        referral_id: referral.id,
        clinician_id: clinicianId,
      });
      toast.success(t('referrals.toast_assigned'));
    } catch (err) {
      console.error('assign referral failed', err);
      toast.error(t('referrals.assign_error'));
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!referral) return;

    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError(t('referrals.errors.name_required'));
      return;
    }

    const cprTrimmed = cpr.trim();
    const parent1CprTrimmed = parent1Cpr.trim();
    const parent2CprTrimmed = parent2Cpr.trim();
    for (const value of [cprTrimmed, parent1CprTrimmed, parent2CprTrimmed]) {
      if (value.length > 0 && !isAcceptableCpr(value)) {
        setSubmitError(t('referrals.errors.cpr_format'));
        return;
      }
    }

    try {
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        referrer_name: referrerName.trim() || null,
        referrer_type: referrerType || null,
        referrer_reference: referrerReference.trim() || null,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        reason: reason.trim() || null,
        notes: notes.trim() || null,
        ...(cprTrimmed.length > 0 ? { cpr: cprTrimmed } : {}),
        secretary_name: secretaryName.trim() || null,
        psychologist_name: psychologistName.trim() || null,
        id_number: idNumber.trim() || null,
        case_number: caseNumber.trim() || null,
        sender_hospital: senderHospital.trim() || null,
        sender_department: senderDepartment.trim() || null,
        referring_doctor_name: referringDoctorName.trim() || null,
        referring_doctor_sor: referringDoctorSor.trim() || null,
        parent_1_name: parent1Name.trim() || null,
        parent_2_name: parent2Name.trim() || null,
        ...(parent1CprTrimmed.length > 0 ? { parent_1_cpr: parent1CprTrimmed } : {}),
        ...(parent2CprTrimmed.length > 0 ? { parent_2_cpr: parent2CprTrimmed } : {}),
        ...(medcomReceived
          ? {}
          : {
              sent_at: fromDatetimeLocal(sentAt),
              referral_diagnosis: referralDiagnosis.trim() || null,
              ambulant: ambulant === 'true' ? true : ambulant === 'false' ? false : null,
              requested_examination: requestedExamination.trim() || null,
              anamnese: anamnese.trim() || null,
            }),
      });
      toast.success(t('referrals.toast_updated'));
      setEditing(false);
      setCpr('');
      setParent1Cpr('');
      setParent2Cpr('');
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
      setSubmitError(t('referrals.update_error'));
    }
  }

  const pending =
    acceptReferral.isPending || updateReferral.isPending || assignReferral.isPending;

  const isNew = referral.status === 'new';
  const isAccepted = referral.status === 'accepted';

  return (
    <PageLayout>
      <div>
          <button
            type="button"
            onClick={() => navigate('/henvisninger')}
            className="mb-4 inline-flex items-center text-sm text-ds-muted hover:text-ds-text"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('referrals.back_to_list')}
          </button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <h2 className={PAGE_TITLE}>
                {referral.first_name} {referral.last_name}
              </h2>
              <ReferralStatusBadge status={referral.status} />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ds-muted">
                  Info-indsamling:
                </span>
                <InfoCollectionProgress referral={referral} />
              </div>
              <div className="flex flex-wrap gap-2">
                {!editing && (
                  <Button
                    variant="secondary"
                    onClick={() => void handlePrint()}
                    disabled={printing}
                    data-testid="referral-print"
                  >
                    {printing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="mr-2 h-4 w-4" />
                    )}
                    {t('referrals.print_button')}
                  </Button>
                )}
                {!editing && (
                  <Button onClick={() => setEditing(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    {t('common.edit')}
                  </Button>
                )}
                {editing && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditing(false);
                      setSubmitError(null);
                      if (referral) {
                        setReferrerName(referral.referrer_name ?? '');
                        setReferrerType(referral.referrer_type ?? '');
                        setReferrerReference(referral.referrer_reference ?? '');
                        setFirstName(referral.first_name);
                        setLastName(referral.last_name);
                        setCpr('');
                        setPhone(referral.phone ?? '');
                        setEmail(referral.email ?? '');
                        setReason(referral.reason ?? '');
                        setNotes(referral.notes ?? '');
                        setSecretaryName(referral.secretary_name ?? '');
                        setPsychologistName(referral.psychologist_name ?? '');
                        setIdNumber(referral.id_number ?? '');
                        setCaseNumber(referral.case_number ?? '');
                        setSenderHospital(referral.sender_hospital ?? '');
                        setSenderDepartment(referral.sender_department ?? '');
                        setSentAt(toDatetimeLocal(referral.sent_at));
                        setReferringDoctorName(referral.referring_doctor_name ?? '');
                        setReferringDoctorSor(referral.referring_doctor_sor ?? '');
                        setParent1Name(referral.parent_1_name ?? '');
                        setParent1Cpr('');
                        setParent2Name(referral.parent_2_name ?? '');
                        setParent2Cpr('');
                        setReferralDiagnosis(referral.referral_diagnosis ?? '');
                        setAmbulant(
                          referral.ambulant === true
                            ? 'true'
                            : referral.ambulant === false
                              ? 'false'
                              : '',
                        );
                        setRequestedExamination(referral.requested_examination ?? '');
                        setAnamnese(referral.anamnese ?? '');
                      }
                    }}
                    disabled={updateReferral.isPending}
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('common.cancel')}
                  </Button>
                )}
                {isNew && (
                  <>
                    <Button onClick={handleAccept} disabled={pending}>
                      {acceptReferral.isPending ? t('common.saving') : t('referrals.accept')}
                    </Button>
                    <Button variant="secondary" onClick={handleDecline} disabled={pending}>
                      {t('referrals.decline')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <p className="mt-1 text-sm text-ds-muted">
            {t('referrals.received_label')}{' '}
            {formatReferralDate(referral.received_at) ?? '—'}
            {isAccepted && referral.patient_id && (
              <>
                {' · '}
                <Link
                  to={`/patients/${referral.patient_id}`}
                  className="text-ds-teal hover:underline"
                >
                  {t('referrals.view_patient')}
                </Link>
              </>
            )}
          </p>

          {referral.clinic_id ? (
            <div className="mt-s4">
              <EntityTasksPanel
                clinicId={referral.clinic_id}
                entityType="referral"
                entityId={referral.id}
              />
            </div>
          ) : null}

          {submitError && (
            <div className="mt-6">
              <ErrorAlert message={submitError} />
            </div>
          )}

          {/* Info slots at the top */}
          {editing ? (
            <form
              onSubmit={onSubmit}
              className="mt-6 space-y-6 rounded-lg border border-ds-line bg-ds-card p-6 shadow-card"
            >
              <EditSection title={t('referrals.section_referrer')}>
                <EditGrid>
                  <Input
                    label={t('referrals.field_referrer_name')}
                    value={referrerName}
                    onChange={(e) => setReferrerName(e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('referrals.field_referrer_type')}
                    </label>
                    <select
                      value={referrerType}
                      onChange={(e) => setReferrerType(e.target.value)}
                      className="mt-1 block h-[44px] w-full rounded-md border border-ds-field bg-ds-card px-3 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    >
                      <option value="">{t('referrals.referrer_type_none')}</option>
                      <option value="egen_laege">{t('referrals.referrer_type_egen_laege')}</option>
                      <option value="speciallaege">{t('referrals.referrer_type_speciallaege')}</option>
                      <option value="selvhenvist">{t('referrals.referrer_type_selvhenvist')}</option>
                      <option value="anden">{t('referrals.referrer_type_anden')}</option>
                    </select>
                  </div>
                  <Input
                    label={t('referrals.field_referrer_reference')}
                    value={referrerReference}
                    onChange={(e) => setReferrerReference(e.target.value)}
                    hint={t('referrals.field_referrer_reference_hint')}
                  />
                </EditGrid>
              </EditSection>

              <EditSection title={t('referrals.section_patient')}>
                <EditGrid>
                  <Input
                    label={t('referrals.field_first_name')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <Input
                    label={t('referrals.field_last_name')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                  <Input
                    label={t('referrals.field_cpr')}
                    value={cpr}
                    onChange={(e) => setCpr(e.target.value)}
                    placeholder="1234567890"
                    hint={
                      referral.has_cpr ? t('referrals.cpr_present') : t('referrals.field_cpr_hint')
                    }
                  />
                  <Input
                    label={t('referrals.field_phone')}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <Input
                    label={t('referrals.field_email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </EditGrid>
              </EditSection>

              <EditSection title={t('referrals.section_sender')}>
                <EditGrid>
                  <Input
                    label={t('referrals.field_sender_hospital')}
                    value={senderHospital}
                    onChange={(e) => setSenderHospital(e.target.value)}
                  />
                  <Input
                    label={t('referrals.field_sender_department')}
                    value={senderDepartment}
                    onChange={(e) => setSenderDepartment(e.target.value)}
                  />
                  <DanishDateTimeField
                    className="sm:col-span-2"
                    name="sent-at"
                    label={t('referrals.field_sent_at')}
                    value={sentAt}
                    onChange={setSentAt}
                    disabled={medcomReceived}
                  />
                  {medcomReceived ? (
                    <p className="sm:col-span-2 text-xs text-ds-muted" data-testid="medcom-received-field-lock">
                      {medcomLockHint}
                    </p>
                  ) : null}
                </EditGrid>
              </EditSection>

              <EditSection title={t('referrals.section_referring_doctor')}>
                <EditGrid>
                  <Input
                    label={t('referrals.field_referring_doctor_name')}
                    value={referringDoctorName}
                    onChange={(e) => setReferringDoctorName(e.target.value)}
                  />
                  <Input
                    label={t('referrals.field_referring_doctor_sor')}
                    value={referringDoctorSor}
                    onChange={(e) => setReferringDoctorSor(e.target.value)}
                  />
                </EditGrid>
              </EditSection>

              <EditSection title={t('referrals.section_parents')}>
                <EditGrid>
                  <Input
                    label={t('referrals.field_parent_1_name')}
                    value={parent1Name}
                    onChange={(e) => setParent1Name(e.target.value)}
                  />
                  <Input
                    label={t('referrals.field_parent_1_cpr')}
                    value={parent1Cpr}
                    onChange={(e) => setParent1Cpr(e.target.value)}
                    placeholder="1234567890"
                    hint={
                      referral.has_parent_1_cpr
                        ? t('referrals.cpr_present')
                        : t('referrals.field_cpr_hint')
                    }
                  />
                  <Input
                    label={t('referrals.field_parent_2_name')}
                    value={parent2Name}
                    onChange={(e) => setParent2Name(e.target.value)}
                  />
                  <Input
                    label={t('referrals.field_parent_2_cpr')}
                    value={parent2Cpr}
                    onChange={(e) => setParent2Cpr(e.target.value)}
                    placeholder="1234567890"
                    hint={
                      referral.has_parent_2_cpr
                        ? t('referrals.cpr_present')
                        : t('referrals.field_cpr_hint')
                    }
                  />
                </EditGrid>
              </EditSection>

              <EditSection title={t('referrals.section_letter')}>
                <div className="space-y-4">
                  <Input
                    label={t('referrals.field_referral_diagnosis')}
                    value={referralDiagnosis}
                    onChange={(e) => setReferralDiagnosis(e.target.value)}
                    placeholder={t('referrals.field_referral_diagnosis_placeholder')}
                    disabled={medcomReceived}
                    hint={medcomReceived ? medcomLockHint : undefined}
                    data-testid="referral-diagnosis-input"
                  />
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('referrals.field_ambulant')}
                    </label>
                    <select
                      value={ambulant}
                      onChange={(e) => setAmbulant(e.target.value as '' | 'true' | 'false')}
                      disabled={medcomReceived}
                      className="mt-1 block h-[44px] w-full rounded-md border border-ds-field bg-ds-card px-3 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:ring-ds-teal disabled:bg-ds-sunk disabled:text-ds-muted sm:max-w-xs"
                    >
                      <option value="">{t('referrals.ambulant_unknown')}</option>
                      <option value="true">{t('referrals.ambulant_yes')}</option>
                      <option value="false">{t('referrals.ambulant_no')}</option>
                    </select>
                    {medcomReceived ? (
                      <p className="mt-1 text-xs text-ds-muted">{medcomLockHint}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('referrals.field_requested_examination')}
                    </label>
                    <textarea
                      rows={3}
                      value={requestedExamination}
                      onChange={(e) => setRequestedExamination(e.target.value)}
                      disabled={medcomReceived}
                      className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal disabled:bg-ds-sunk disabled:text-ds-muted"
                      placeholder={t('referrals.field_requested_examination_placeholder')}
                    />
                    {medcomReceived ? (
                      <p className="mt-1 text-xs text-ds-muted">{medcomLockHint}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('referrals.field_anamnese')}
                    </label>
                    <textarea
                      rows={6}
                      value={anamnese}
                      onChange={(e) => setAnamnese(e.target.value)}
                      disabled={medcomReceived}
                      className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal disabled:bg-ds-sunk disabled:text-ds-muted"
                      placeholder={t('referrals.field_anamnese_placeholder')}
                    />
                    {medcomReceived ? (
                      <p className="mt-1 text-xs text-ds-muted">{medcomLockHint}</p>
                    ) : null}
                  </div>
                </div>
              </EditSection>

              <EditSection title={t('referrals.section_details')}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('referrals.field_reason')}
                    </label>
                    <textarea
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                      placeholder={t('referrals.field_reason_placeholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('referrals.field_notes')}
                    </label>
                    <textarea
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                      placeholder={t('referrals.field_notes_placeholder')}
                    />
                  </div>
                </div>
              </EditSection>

              <EditSection title="Administrativ information">
                <EditGrid>
                  <Input
                    label="Sekretær"
                    value={secretaryName}
                    onChange={(e) => setSecretaryName(e.target.value)}
                  />
                  <Input
                    label="Psykolog"
                    value={psychologistName}
                    onChange={(e) => setPsychologistName(e.target.value)}
                  />
                  <Input
                    label="ID #"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                  />
                  <Input
                    label="Sagsnr."
                    value={caseNumber}
                    onChange={(e) => setCaseNumber(e.target.value)}
                  />
                </EditGrid>
              </EditSection>

              <div className="flex justify-end gap-3 border-t border-ds-line pt-4">
                <Button type="submit" disabled={updateReferral.isPending}>
                  {updateReferral.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('common.save')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <ReferralOverviewCard referral={referral} canRevealCpr={canRevealCpr} />
          )}

          {/* Tab Navigation */}
          <div className="mt-4">
            <div className="border-b border-ds-line">
              <nav className="-mb-px flex space-x-4 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'details'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                >
                  {t('referrals.tab_letter_details')}
                </button>
                <button
                  onClick={() => setActiveTab('sagsakter')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'sagsakter'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                >
                  Sagsakter (NPU)
                </button>
                <button
                  onClick={() => setActiveTab('files')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'files'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                >
                  {t('referrals.tab_files')}
                </button>
                <button
                  onClick={() => setActiveTab('relations')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'relations'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                >
                  {t('patients.tabs.relations')}
                </button>
                <button
                  onClick={() => setActiveTab('lpr')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'lpr'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                >
                  LPR
                </button>
                <button
                  onClick={() => setActiveTab('assignment')}
                  className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'assignment'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                >
                  Tildeling
                </button>
                {referral.source === 'medcom' && (
                  <button
                    onClick={() => setActiveTab('medcom')}
                    className={`
                    whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                    ${
                      activeTab === 'medcom'
                        ? 'border-ds-teal text-ds-teal  '
                        : 'border-transparent text-ds-muted hover:border-ds-field hover:text-ds-text   '
                    }
                  `}
                  >
                    {t('referrals.tab_medcom')}
                  </button>
                )}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === 'details' && <ReferralLetterDetails referral={referral} />}

              {activeTab === 'sagsakter' && <ReferralSagsakterTab referral={referral} />}

              {activeTab === 'files' && <ReferralFilesTab referral={referral} />}

              {activeTab === 'relations' &&
                (referral.patient_id ? (
                  <RelationsTab patientId={referral.patient_id} clinicId={referral.clinic_id} />
                ) : (
                  <section className="rounded-lg border border-ds-line bg-ds-card p-12 text-center shadow-card">
                    <h2 className="text-sm font-medium text-ds-text">
                      {t('referrals.relations_needs_patient_title')}
                    </h2>
                    <p className="mt-2 text-sm text-ds-muted">
                      {t('referrals.relations_needs_patient_description')}
                    </p>
                  </section>
                ))}

              {activeTab === 'lpr' && <ReferralLprTab referral={referral} />}

              {activeTab === 'assignment' && (
                <ReferralAssignmentPanel
                  clinicId={referral.clinic_id}
                  assignedClinicianId={referral.assigned_clinician_id}
                  pending={pending}
                  onAssign={(clinicianId) => void handleAssign(clinicianId)}
                />
              )}

              {activeTab === 'medcom' && referral.source === 'medcom' && (
                <div className="space-y-4">
                  {referral.medcom_type_code === 'XREF01' && (
                    <>
                      <MedcomXref01Details referral={referral} />
                      <ReferralReplyPanel referral={referral} />
                      <EpikrisePanel referral={referral} />
                      <ReferralRevisitPanel referral={referral} />
                    </>
                  )}
                  {/* T1.7 (DIS05-S 4.1–4.3): a received XREF02 shows its fields and can be answered with an epikrise. */}
                  {referral.medcom_type_code === 'XREF02' && (
                    <>
                      <MedcomXref01Details referral={referral} />
                      <EpikrisePanel referral={referral} />
                    </>
                  )}
                  <MedcomReferralSections referral={referral} canRevealCpr={canRevealCpr} />
                </div>
              )}
            </div>
          </div>
        </div>
    </PageLayout>
  );
}

type MedcomPhoneQualifier = 'arbejde' | 'fastnet' | 'mobil';
type MedcomRelationCode = 'mor' | 'far' | 'barn' | 'uspec_paaroerende';

function formatDanishAddress(address: ReferralPatientAddress): string | null {
  const street = address.streetName?.trim() ?? '';
  const suburb = address.suburbName?.trim() ?? '';
  const postCode = address.postCodeIdentifier?.trim() ?? '';
  const district = address.districtName?.trim() ?? '';
  const postal = [postCode, district].filter((part) => part.length > 0).join(' ');
  const lines = [street, suburb, postal].filter((line) => line.length > 0);
  return lines.length > 0 ? lines.join('\n') : null;
}

function formatMedcomDiagnosis(code: string | null, text: string | null): string | null {
  if (code && text) return `${code} — ${text}`;
  if (code) return code;
  return text;
}

function formatAttachmentType(attachment: ReferralAttachment): string | null {
  const code = attachment.objectCode?.trim() ?? '';
  const extension = attachment.objectExtensionCode?.trim() ?? '';
  if (code.length > 0 && extension.length > 0) return `${code} · ${extension}`;
  if (code.length > 0) return code;
  if (extension.length > 0) return extension;
  return null;
}

function medcomPhoneQualifier(code: string | undefined): MedcomPhoneQualifier | null {
  const normalized = code?.trim().toLowerCase() ?? '';
  switch (normalized) {
    case 'arbejde':
      return 'arbejde';
    case 'fastnet':
      return 'fastnet';
    case 'mobil':
      return 'mobil';
    default:
      return null;
  }
}

function medcomPhoneQualifierKey(
  code: MedcomPhoneQualifier,
): 'referrals.medcom_phone_work' | 'referrals.medcom_phone_landline' | 'referrals.medcom_phone_mobile' {
  switch (code) {
    case 'arbejde':
      return 'referrals.medcom_phone_work';
    case 'fastnet':
      return 'referrals.medcom_phone_landline';
    case 'mobil':
      return 'referrals.medcom_phone_mobile';
    default: {
      const exhaustive: never = code;
      return exhaustive;
    }
  }
}

function phoneQualifierText(
  code: string | undefined,
  translate: (key: string) => string,
): string | null {
  const known = medcomPhoneQualifier(code);
  if (known) return translate(medcomPhoneQualifierKey(known));
  const raw = code?.trim() ?? '';
  return raw.length > 0 ? raw : null;
}

function formatPhoneLine(phone: ReferralPatientPhone, qualifier: string | null): string {
  const number = phone.telephoneSubscriberIdentifier?.trim() ?? '';
  if (number.length > 0 && qualifier) return `${number} (${qualifier})`;
  if (number.length > 0) return number;
  if (qualifier) return qualifier;
  return '—';
}

function medcomRelationCode(code: string | null): MedcomRelationCode | null {
  switch (code) {
    case 'mor':
    case 'far':
    case 'barn':
    case 'uspec_paaroerende':
      return code;
    default:
      return null;
  }
}

function medcomRelationKey(
  code: string | null,
):
  | 'referrals.medcom_relation_mother'
  | 'referrals.medcom_relation_father'
  | 'referrals.medcom_relation_child'
  | 'referrals.medcom_relation_unspecified'
  | null {
  const known = medcomRelationCode(code);
  if (!known) return null;
  switch (known) {
    case 'mor':
      return 'referrals.medcom_relation_mother';
    case 'far':
      return 'referrals.medcom_relation_father';
    case 'barn':
      return 'referrals.medcom_relation_child';
    case 'uspec_paaroerende':
      return 'referrals.medcom_relation_unspecified';
    default: {
      const exhaustive: never = known;
      return exhaustive;
    }
  }
}

function answerCcHasValue(receiver: ReferralAnswerCcReceiver): boolean {
  return Boolean(
    receiver.identifier ||
      receiver.identifierCode ||
      receiver.organisationName ||
      receiver.departmentName ||
      receiver.unitName ||
      receiver.streetName ||
      receiver.suburbName ||
      receiver.districtName ||
      receiver.postCodeIdentifier ||
      receiver.telephoneSubscriberIdentifier,
  );
}

function isHttpUrl(url: string): boolean {
  return url.startsWith('https://') || url.startsWith('http://');
}

function attachmentUrlValue(url: string | undefined): React.ReactNode {
  if (!url) return <div className="text-sm font-medium">—</div>;
  // Only http(s) is linked: the value is an untrusted URL from an imported document.
  if (!isHttpUrl(url)) return <div className="break-all text-sm font-medium">{url}</div>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="break-all text-ds-teal hover:underline"
    >
      {url}
    </a>
  );
}

const READ_ONLY_SECTION =
  'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card';
const READ_ONLY_GRID =
  'grid gap-px bg-ds-line sm:grid-cols-2 lg:grid-cols-4';
const READ_ONLY_HEADING =
  'text-xs font-medium uppercase tracking-wide text-ds-muted';

function MedcomReferralSections({
  referral,
  canRevealCpr,
}: {
  referral: Referral;
  canRevealCpr: boolean;
}) {
  const { t } = useTranslation();
  const address = formatDanishAddress(referral.patient_address);
  const diagnosis = formatMedcomDiagnosis(
    referral.referral_diagnosis_code,
    referral.referral_diagnosis,
  );
  const relationKey = medcomRelationKey(referral.relative_relation_code);
  const receivedAttachments = useReceivedReferralAttachments(
    referral.id,
    referral.source === 'medcom' && referral.attachments.some((a) => Boolean(a.objectIdentifier)),
  );
  const relationText = relationKey ? t(relationKey) : referral.relative_relation_code;
  const copyReceiver = referral.answer_cc_receiver;
  // C7: XREF01 shows its Receiver in MedcomXref01Details; the others read it
  // from the stored letter.
  const letterReceiver = useReferralLetterReceiver(referral.id, referral.medcom_type_code !== 'XREF01' && referral.medcom_type_code !== 'XREF02');
  const senderIdentity = referralSenderIdentity(referral);
  const senderIdentifierValue = formatMedcomIdentifier(
    senderIdentity.identifier,
    senderIdentity.identifierCode,
  );
  const receiverValue = (value: string | null | undefined) => (
    <div className="text-sm font-medium">
      {letterReceiver.isLoading ? '…' : value ?? '—'}
    </div>
  );

  return (
    <div className="space-y-4">
      <section className={READ_ONLY_SECTION}>
        <div className="border-b border-ds-line px-4 py-2.5">
          <span className={READ_ONLY_HEADING}>{t('referrals.section_patient')}</span>
        </div>
        <div className={READ_ONLY_GRID}>
          <InfoCell
            label={t('referrals.medcom_field_job_title')}
            value={<div className="text-sm font-medium">{referral.patient_job_title ?? '—'}</div>}
          />
          <InfoCell
            label={t('referrals.medcom_field_address')}
            value={
              <div className="whitespace-pre-wrap text-sm font-medium">{address ?? '—'}</div>
            }
          />
          <InfoCell
            label={t('referrals.field_phone')}
            value={
              referral.patient_phones.length === 0 ? (
                <div className="text-sm font-medium">—</div>
              ) : (
                <div className="space-y-1">
                  {referral.patient_phones.map((phone, index) => (
                    <div key={index} className="text-sm font-medium">
                      {formatPhoneLine(phone, phoneQualifierText(phone.telephoneSubscriberCode, t))}
                    </div>
                  ))}
                </div>
              )
            }
          />
          <InfoCell
            label={t('referrals.field_email')}
            value={<div className="text-sm font-medium">{referral.email ?? '—'}</div>}
          />
        </div>
      </section>

      <section className={READ_ONLY_SECTION}>
        <div className="border-b border-ds-line px-4 py-2.5">
          <span className={READ_ONLY_HEADING}>{t('referrals.medcom_section_relative')}</span>
        </div>
        <div className={READ_ONLY_GRID}>
          <InfoCell
            label={t('referrals.medcom_field_relative_name')}
            value={
              <div className="space-y-1">
                <div className="text-sm font-medium">{referral.parent_1_name ?? '—'}</div>
                {referral.has_parent_1_cpr && (
                  <ReferralCprBadge referralId={referral.id} subject="parent_1" canReveal={canRevealCpr} />
                )}
              </div>
            }
          />
          <InfoCell
            label={t('referrals.medcom_field_relation')}
            value={<div className="text-sm font-medium">{relationText ?? '—'}</div>}
          />
        </div>
      </section>

      <section className={READ_ONLY_SECTION}>
        <LetterDetailBlock
          label={t('referrals.field_referral_diagnosis')}
          value={diagnosis}
          last
        />
      </section>

      <section className={READ_ONLY_SECTION}>
        <div className="border-b border-ds-line px-6 py-2.5">
          <span className={READ_ONLY_HEADING}>{t('referrals.medcom_section_clinical')}</span>
        </div>
        <LetterDetailBlock label={t('referrals.field_anamnese')} value={referral.anamnese} />
        <LetterDetailBlock label={t('referrals.medcom_field_cave')} value={referral.cave} />
        <LetterDetailBlock
          label={t('referrals.medcom_field_examination')}
          value={referral.examination}
        />
        <LetterDetailBlock
          label={t('referrals.medcom_field_medicine')}
          value={referral.actual_medicine}
          last
        />
      </section>

      <section className={READ_ONLY_SECTION}>
        {referral.attachments.length === 0 ? (
          <LetterDetailBlock
            label={t('referrals.medcom_section_attachments')}
            value={null}
            last
          />
        ) : (
          <>
            <div className="border-b border-ds-line px-4 py-2.5">
              <span className={READ_ONLY_HEADING}>{t('referrals.medcom_section_attachments')}</span>
            </div>
            {referral.attachments.map((attachment, index) => (
            <div
              key={`${attachment.objectIdentifier ?? 'attachment'}-${index}`}
              className={
                index === referral.attachments.length - 1
                  ? ''
                  : 'border-b border-ds-line '
              }
            >
              <div className={READ_ONLY_GRID}>
                <InfoCell
                  label={t('referrals.medcom_attachment_description')}
                  value={
                    <div className="text-sm font-medium">{attachment.refDescription ?? '—'}</div>
                  }
                />
                <InfoCell
                  label={t('referrals.medcom_attachment_type')}
                  value={
                    <div className="text-sm font-medium">
                      {formatAttachmentType(attachment) ?? '—'}
                    </div>
                  }
                />
                <InfoCell
                  label={t('referrals.medcom_attachment_size')}
                  value={
                    <div className="text-sm font-medium">
                      {attachment.originalObjectSize ?? '—'}
                    </div>
                  }
                />
                <InfoCell
                  label={t('referrals.medcom_attachment_url')}
                  value={attachmentUrlValue(attachment.url)}
                />
                <InfoCell
                  label={t('referrals.medcom_attachment_identifier')}
                  value={
                    <div className="break-all text-sm font-medium">
                      {attachment.objectIdentifier ?? '—'}
                    </div>
                  }
                />
                {attachment.objectIdentifier && (
                  <InfoCell
                    label={t('referrals.medcom_attachment_file')}
                    value={
                      <ReceivedAttachmentFile
                        referralId={referral.id}
                        loading={receivedAttachments.isLoading}
                        attachment={receivedAttachments.data?.find(
                          (r) =>
                            normalizeObjectIdentifier(r.object_identifier) ===
                            normalizeObjectIdentifier(attachment.objectIdentifier),
                        )}
                      />
                    }
                  />
                )}
              </div>
            </div>
            ))}
          </>
        )}
      </section>

      <section className={READ_ONLY_SECTION}>
        <div className="border-b border-ds-line px-4 py-2.5">
          <span className={READ_ONLY_HEADING}>{t('referrals.section_sender')}</span>
        </div>
        <div className={READ_ONLY_GRID}>
          <InfoCell
            label={t('referrals.medcom_field_sender_ean')}
            value={<div className="text-sm font-medium">{referral.sender_ean ?? '—'}</div>}
          />
          <InfoCell
            label={t('referrals.field_sender_hospital')}
            value={<div className="text-sm font-medium">{referral.sender_hospital ?? '—'}</div>}
          />
          <InfoCell
            label={t('referrals.field_sender_department')}
            value={<div className="text-sm font-medium">{referral.sender_department ?? '—'}</div>}
          />
          <InfoCell
            label={t('referrals.field_referring_doctor_name')}
            value={
              <div className="text-sm font-medium">{referral.referring_doctor_name ?? '—'}</div>
            }
          />
          <InfoCell
            label={t(senderIdentifierHeadingKey(senderIdentity.identifierCode))}
            value={
              <div className="text-sm font-medium">{senderIdentifierValue ?? '—'}</div>
            }
          />
        </div>
      </section>

      {referral.medcom_type_code !== 'XREF01' && referral.medcom_type_code !== 'XREF02' && (
        <section className={READ_ONLY_SECTION} data-testid="medcom-receiver-section">
          <div className="border-b border-ds-line px-4 py-2.5">
            <span className={READ_ONLY_HEADING}>{t('referrals.xref01_section_receiver')}</span>
          </div>
          {letterReceiver.isError ? (
            <LetterDetailBlock
              label={t('referrals.xref01_section_receiver')}
              value={t('referrals.print_receiver_unavailable')}
              last
            />
          ) : (
            <div className={READ_ONLY_GRID}>
              <InfoCell label={t('referrals.xref01_ean')} value={receiverValue(letterReceiver.data?.ean)} />
              <InfoCell
                label={t('referrals.xref01_identifier')}
                value={receiverValue(
                  [letterReceiver.data?.identifier, letterReceiver.data?.identifier_code].filter(Boolean).join(' · ') || null,
                )}
              />
              <InfoCell
                label={t('referrals.xref01_organisation')}
                value={receiverValue(letterReceiver.data?.organisation_name)}
              />
              <InfoCell
                label={t('referrals.xref01_department')}
                value={receiverValue(letterReceiver.data?.department_name)}
              />
              <InfoCell label={t('referrals.xref01_unit')} value={receiverValue(letterReceiver.data?.unit_name)} />
              <InfoCell
                label={t('referrals.xref01_address')}
                value={receiverValue(
                  [
                    letterReceiver.data?.street_name,
                    letterReceiver.data?.suburb_name,
                    [letterReceiver.data?.post_code, letterReceiver.data?.district_name].filter(Boolean).join(' '),
                  ].filter(Boolean).join(', ') || null,
                )}
              />
            </div>
          )}
        </section>
      )}

      {answerCcHasValue(copyReceiver) ? (
        <section className={READ_ONLY_SECTION}>
          <div className="border-b border-ds-line px-4 py-2.5">
            <span className={READ_ONLY_HEADING}>{t('referrals.medcom_section_cc')}</span>
          </div>
          <div className={READ_ONLY_GRID}>
            <InfoCell
              label={t('referrals.medcom_cc_identifier')}
              value={<div className="text-sm font-medium">{copyReceiver.identifier ?? '—'}</div>}
            />
            <InfoCell
              label={t('referrals.medcom_cc_identifier_code')}
              value={
                <div className="text-sm font-medium">{copyReceiver.identifierCode ?? '—'}</div>
              }
            />
            <InfoCell
              label={t('referrals.medcom_cc_organisation')}
              value={
                <div className="text-sm font-medium">{copyReceiver.organisationName ?? '—'}</div>
              }
            />
            <InfoCell
              label={t('referrals.field_sender_department')}
              value={
                <div className="text-sm font-medium">{copyReceiver.departmentName ?? '—'}</div>
              }
            />
            <InfoCell
              label={t('referrals.medcom_cc_unit')}
              value={<div className="text-sm font-medium">{copyReceiver.unitName ?? '—'}</div>}
            />
            <InfoCell
              label={t('referrals.medcom_cc_street')}
              value={<div className="text-sm font-medium">{copyReceiver.streetName ?? '—'}</div>}
            />
            <InfoCell
              label={t('referrals.medcom_cc_suburb')}
              value={<div className="text-sm font-medium">{copyReceiver.suburbName ?? '—'}</div>}
            />
            <InfoCell
              label={t('referrals.medcom_cc_district')}
              value={<div className="text-sm font-medium">{copyReceiver.districtName ?? '—'}</div>}
            />
            <InfoCell
              label={t('referrals.medcom_cc_postcode')}
              value={
                <div className="text-sm font-medium">{copyReceiver.postCodeIdentifier ?? '—'}</div>
              }
            />
            <InfoCell
              label={t('referrals.field_phone')}
              value={
                <div className="text-sm font-medium">
                  {copyReceiver.telephoneSubscriberIdentifier ?? '—'}
                </div>
              }
            />
          </div>
        </section>
      ) : (
        <section className={READ_ONLY_SECTION}>
          <LetterDetailBlock
            label={t('referrals.medcom_section_cc')}
            value={t('referrals.medcom_cc_empty')}
            last
          />
        </section>
      )}

      <section className={READ_ONLY_SECTION}>
        <div className="border-b border-ds-line px-4 py-2.5">
          <span className={READ_ONLY_HEADING}>{t('referrals.medcom_section_technical')}</span>
        </div>
        <div className={READ_ONLY_GRID}>
          <InfoCell
            label={t('referrals.medcom_field_version')}
            value={
              <div className="text-sm font-medium">{referral.medcom_version_code ?? '—'}</div>
            }
          />
          <InfoCell
            label={t('referrals.medcom_field_letter_id')}
            value={
              <div className="break-all text-sm font-medium">
                {referral.medcom_letter_identifier ?? '—'}
              </div>
            }
          />
          <InfoCell
            label={t('referrals.medcom_field_exchange_id')}
            value={
              <div className="break-all text-sm font-medium">
                {referral.medcom_exchange_id ?? '—'}
              </div>
            }
          />
          <InfoCell
            label={t('referrals.medcom_field_episode_of_care_id')}
            value={
              <div className="break-all font-mono text-sm font-medium" data-testid="referral-episode-of-care-id">
                {referral.episode_of_care_identifier ?? '—'}
              </div>
            }
          />
        </div>
      </section>
    </div>
  );
}

function EditSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-ds-text">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function EditGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function InfoCell({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-ds-card px-4 py-3  ${className ?? ''}`}>
      <div className="mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ds-muted">
          {label}
        </span>
      </div>
      <div className="text-sm text-ds-text">{value}</div>
    </div>
  );
}

function InfoCollectionProgress({ referral }: { referral: { consent_collected: boolean; info_letter_sent: boolean; agreement_made: boolean; payment_arranged: boolean } }) {
  const fields = [
    referral.consent_collected,
    referral.info_letter_sent,
    referral.agreement_made,
    referral.payment_arranged,
  ];
  
  const collected = fields.filter(Boolean).length;
  const total = 4;

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-ds-muted">
        {collected}/{total}
      </span>
      <div className="flex gap-0.5">
        {fields.map((isChecked, i) => (
          <div
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${
              isChecked
                ? 'bg-ds-green '
                : 'bg-ds-neutral-tint '
            }`}
          />
        ))}
      </div>
    </div>
  );
}
